/**
 * Plain-language copy for the Campaign Builder's dropdown settings.
 *
 * Comms 9/23 review: every dropdown reads left to right (the choice on the
 * left, what it means on the right), and the words have to make sense to
 * someone who has never opened Google Ads. Keeping the option labels and
 * their explanations side by side here means a label can't change without
 * its explanation being in view, and one test can hold all of it to the
 * plain-copy rules (see __tests__/campaignBuilderCopy.test.ts).
 *
 * Keys are the enum values the backend accepts; only the words are ours.
 */

export interface SettingOptionCopy {
  /** Text shown in the dropdown. */
  label: string;
  /** Shown to the right of the dropdown while this option is selected. */
  explanation: string;
}

export const CAMPAIGN_TYPE_INTRO = 'What kind of campaign to build.';

export const CAMPAIGN_TYPE_COPY: Record<'SEARCH' | 'PERFORMANCE_MAX', SettingOptionCopy> = {
  SEARCH: {
    label: 'Search',
    explanation:
      'Text ads that show when people search Google for your keywords. The right choice for most home-service clients.',
  },
  PERFORMANCE_MAX: {
    label: 'Performance Max',
    explanation:
      'Google decides where the ads show (Search, Maps, YouTube and more) using the images and text you give it. Needs images and working conversion tracking. You check the ads before anything is created.',
  },
};

export const CAMPAIGN_GOAL_INTRO =
  'What the campaign should get you. The goal picks a bidding strategy for you, and you can still change it.';

export const CAMPAIGN_GOAL_COPY: Record<
  | 'ROI_HOME_SERVICES'
  | 'LEAD_GENERATION'
  | 'BRAND_AWARENESS'
  | 'TRAFFIC_CLICKS'
  | 'MANUAL_TESTING',
  SettingOptionCopy
> = {
  ROI_HOME_SERVICES: {
    label: 'ROI-focused home services',
    explanation:
      'For home-service businesses that want paying jobs, not just website visits. Google goes after calls and form fills, the leads most likely to turn into work. Needs conversion tracking set up in Google Ads.',
  },
  LEAD_GENERATION: {
    label: 'Lead generation',
    explanation:
      'Google goes after as many calls and form fills as the budget allows. Needs conversion tracking set up in Google Ads.',
  },
  BRAND_AWARENESS: {
    label: 'Brand awareness',
    explanation:
      'Gets the business name in front of more people. Google goes after clicks, not leads.',
  },
  TRAFFIC_CLICKS: {
    label: 'Traffic / clicks',
    explanation:
      'Sends as many people to the website as the budget allows. Google goes after clicks, not leads.',
  },
  MANUAL_TESTING: {
    label: 'Manual / testing',
    explanation: 'For testing. Pick the bidding strategy yourself.',
  },
};

export const BIDDING_STRATEGY_INTRO = 'How Google decides what to pay for each click.';

export const BIDDING_STRATEGY_COPY: Record<
  'MAXIMIZE_CONVERSIONS' | 'TARGET_CPA' | 'TARGET_ROAS' | 'MAXIMIZE_CLICKS',
  SettingOptionCopy
> = {
  MAXIMIZE_CONVERSIONS: {
    label: 'Maximize conversions',
    explanation:
      'Google bids to get the most calls and form fills for the budget. The usual choice for lead campaigns.',
  },
  TARGET_CPA: {
    label: 'Target CPA',
    explanation:
      'Google aims for a set cost per lead. Only use it when the account already has plenty of recent conversions.',
  },
  TARGET_ROAS: {
    label: 'Target ROAS',
    explanation:
      'Google aims for a set return on ad spend. Only use it when conversions have dollar values and the account has plenty of recent history.',
  },
  MAXIMIZE_CLICKS: {
    label: 'Maximize clicks',
    explanation:
      'Google bids to get the most clicks for the budget. Good for new accounts with no conversion history yet.',
  },
};

export const CLUSTER_DEFINITION =
  'A cluster is a group of closely related keywords, for example everything about foundation repair. Each cluster becomes an ad group, or its own campaign if you pick separate campaigns below.';

export const BUDGET_ALLOCATION_INTRO =
  'How the budget is split between clusters. With one campaign, this is a guide for reviewing the ad groups. With separate campaigns, each campaign gets its share as its own budget.';

export const BUDGET_ALLOCATION_COPY: Record<
  'EVEN_BY_AD_GROUP' | 'WEIGHTED_BY_CPC_VOLUME',
  SettingOptionCopy
> = {
  EVEN_BY_AD_GROUP: {
    label: 'Even split',
    explanation: 'Every cluster gets the same share of the budget.',
  },
  WEIGHTED_BY_CPC_VOLUME: {
    label: 'Weighted by CPC x volume',
    explanation:
      'More budget goes to clusters whose keywords get more searches and cost more per click. Each cluster is scored by cost per click times monthly searches, and gets a share of the budget to match.',
  },
};

export const CAMPAIGN_STRUCTURE_INTRO = 'How the clusters are set up in Google Ads.';

export const CAMPAIGN_STRUCTURE_COPY: Record<
  'SINGLE_CAMPAIGN' | 'CAMPAIGN_PER_CLUSTER',
  SettingOptionCopy
> = {
  SINGLE_CAMPAIGN: {
    label: 'One campaign, ad groups',
    explanation:
      'Recommended. Builds one campaign with one budget. Each cluster, for example Foundation and Waterproofing, becomes an ad group inside it.',
  },
  CAMPAIGN_PER_CLUSTER: {
    label: 'One campaign per cluster',
    explanation:
      'Builds a separate campaign for each cluster, for example one for Foundation and one for Waterproofing, each with its own share of the budget.',
  },
};
